import authStoreGetters from '../authentication/store/getters'
import * as Sentry from '@sentry/vue'
import { gql } from '@apollo/client/core'
import {
  ArcClaimDto,
  ArcClaimGetArgs,
  ArcConvertedFileDto,
  ArcDto,
  ArcReviewSiteDto,
  bsConstants,
  CancelClaimDto,
  ClaimDownloadArgs,
  ClaimEligibilityStatusDto,
  CreateClaimDto,
  getArcSitesFromFlags,
  GetClaimEligibilityStatusArgs,
  GroupedArcClaimResultDto,
  HasIndex,
  IdDto,
  isValidReviewLink,
  ReviewExemptionArgs,
  UnCancelClaimDto,
  UserDto
} from 'booksprout'
import { arcFragments, arcReviewClaimFragments } from './fragments'
import { bookFragments } from '../books/bookService'
import { AxiosResponse } from 'axios'
import { countSiteFlags, getArcResult, getClaimResult } from './arcHelpers'
import { useApollo } from '../apollo/apolloModule'
import { useAxios } from '../axios/axiosModule'
import { useBaseCrud } from '../../composables/useBaseCrud'

/**
 * Claims: making one, reading them (singly, listed, grouped), the read/unread/new-activity states, the
 * download and email paths, cancellation, eligibility, and the review-site exemptions an author grants
 * against a claim.
 *
 * One of three services that replace `ArcService` (arc.service.claims.spec.ts is this lane).
 */

const fileDownload = require('js-file-download')

const updateClaimReadLocation = (claim: ArcClaimDto, cfi: string): Promise<ArcClaimDto> => {
  return useApollo().mutate({
    variables: {
      userId: claim.user?.id,
      claimId: claim.id,
      cfi
    },
    mutation: gql`
      mutation UpdateClaimReadLocation (
        $userId: Int!
        $claimId: Int!
        $cfi: String!
      ) {
        updateClaimReadLocation(
          userId: $userId,
          claimId: $claimId,
          cfi: $cfi
        ) {
          ...ArcClaimFragment
        }
      },
      ${arcFragments.claim}
    `
  })
}

const getClaim = (arcId: number, userId: number): Promise<ArcClaimDto | undefined> => {
  return useApollo().query<ArcClaimDto>({
    variables: {
      arcId,
      userId
    },
    query: gql`
      query GetArcClaim (
        $arcId: Int,
        $userId: Int
      ) {
        arcUserClaim (arcId: $arcId, userId: $userId) {
          ...ArcClaimFragment,
          epubFile,
          pdfFile,
          arc {
            ...ArcFragment
          }
        }
      },
      ${arcFragments.claim},
      ${arcFragments.arc}
    `
  }).then(claim => {
    return getClaimResult(claim)
  })
}

const getClaimToReview = (arcId: number, userId: number): Promise<ArcClaimDto> => {
  return useApollo().query<ArcClaimDto>({
    variables: {
      arcId,
      userId
    },
    query: gql`
      query GetArcClaim (
        $arcId: Int,
        $userId: Int
      ) {
        arcUserClaim (arcId: $arcId, userId: $userId) {
          id,
          createdDate,
          status,
          statusDate,
          cancellationReason,
          cancellationType,
          downloadCode,
          downloadCodeExpirationDate,
          smashwordsCouponCode,
          reviewFlags,
          optionalReviewFlags,
          requiredReviewCount,
          selectedAudioSiteFlag,
          canFollowTeam,
          dueDate,
          reviewerStatus,
          review {
            id,
            rating,
            title,
            review,
            reviewLanguage,
            translatedReview,
            privateFeedback,
            disclaimer,
            createdDate,
            updatedDate
            sites {
              id,
              link,
              site,
              status,
              statusDate,
              statusReason,
              exempt
            }
          },
          arc {
            id,
            isDraft,
            status,
            book {
              id,
              bookCover,
              title,
              seriesName,
              seriesNumber,
              penName,
              wordCount,
              authorPenName {
                id,
                name,
                image,
                firstName,
                averageRating,
                followers {
                  id
                }
              },
              linkAmazonAsin,
              linkAmazonAsinPrint,
              linkAudibleUs,
              linkAudibleUk,
              linkBarnes,
              linkBookbub,
              linkGoodreads,
              linkGoogle,
              linkSmashwords,
              linkKobo,
              linkITunes,
              linkITunesAudio,
              linkBarnesAudio,
              linkGoogleAudio,
              linkGoodreadsAudio,
              linkKoboAudio,
              linkBookbubAudio,
              linkStoryGraph,
              linkStoryGraphAudio,
              description,
              audience,
              keywords,
              releaseDate,
              categories {
                id,
                name,
                translations {
                  locale
                  name
                }
              },
              pdfFile,
              pdfFileSizeBytes,
              pdfSystemGenerated,
              epubFile,
              epubFileSizeBytes,
              epubPreviewFile,
              epubSystemGenerated
            },
            arcTeam {
              id,
              name,
              defaultDisclaimer
            }
            specialInformation,
            arcType,
            discoverableType,
            participationType,
            copyCountType,
            copyCount,
            seriesLimitType,
            reviewFlags,
            optionalReviewFlags,
            requiredReviewCount,
            optionalReviewCount,
            liveDate,
            reviewDays,
            stopDownloadDate,
            neverEndingPrivate,
            participationPreventionType,
            siteWideParticipationPreventionType,
            dueDate,
            dueCutOffDate,
            vipExclusiveAccessDays
          }
        }
      }
    `
  }).then(claim => {
    return getClaimResult(claim) as ArcClaimDto
  })
}

const getClaims = (args: ArcClaimGetArgs): Promise<ArcClaimDto[]> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetArcClaims (
        $arcId: Int,
        $status: Int,
        $userSearchNeedle: String,
        $type: Int,
        $forAuthenticatedUser: Boolean,
        $reviewSiteStatus: Int,
        $mustHaveReviews: Boolean,
        $claimId: Int,
        $userId: Int,
        $statuses: [Int!],
        $isDue: Boolean,
        $sortBy: String,
        $genericSearchTerm: String,
        $skip: Int,
        $take: Int,
        $bookId: Int,
        $authorPenNameId: Int
      ) {
        arcClaims (
          arcId: $arcId,
          status: $status,
          userSearchNeedle: $userSearchNeedle,
          type: $type,
          forAuthenticatedUser: $forAuthenticatedUser,
          reviewSiteStatus: $reviewSiteStatus,
          mustHaveReviews: $mustHaveReviews,
          claimId: $claimId,
          userId: $userId,
          statuses: $statuses,
          isDue: $isDue,
          sortBy: $sortBy,
          genericSearchTerm: $genericSearchTerm,
          skip: $skip,
          take: $take,
          bookId: $bookId,
          authorPenNameId: $authorPenNameId
        ) {
          ...ArcReviewClaimFragment
        }
      },
      ${arcReviewClaimFragments.reviewClaim}
    `
  }).then((r: any) => {
    return r.map((m: ArcClaimDto) => getClaimResult(m))
  })
}

/**
 * How many claims `getClaims` finds across every page for the owner of the arcs, with the same filters:
 * the author's reviews list numbers its pages from it. The page and its sort are not sent.
 */
const getArcOwnerClaimsCount = (args: ArcClaimGetArgs): Promise<number> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetArcOwnerClaimsCount (
        $arcId: Int,
        $status: Int,
        $userSearchNeedle: String,
        $type: Int,
        $forAuthenticatedUser: Boolean,
        $reviewSiteStatus: Int,
        $mustHaveReviews: Boolean,
        $claimId: Int,
        $userId: Int,
        $statuses: [Int!],
        $isDue: Boolean,
        $genericSearchTerm: String,
        $bookId: Int,
        $authorPenNameId: Int
      ) {
        arcOwnerClaimsCount (
          arcId: $arcId,
          status: $status,
          userSearchNeedle: $userSearchNeedle,
          type: $type,
          forAuthenticatedUser: $forAuthenticatedUser,
          reviewSiteStatus: $reviewSiteStatus,
          mustHaveReviews: $mustHaveReviews,
          claimId: $claimId,
          userId: $userId,
          statuses: $statuses,
          isDue: $isDue,
          genericSearchTerm: $genericSearchTerm,
          bookId: $bookId,
          authorPenNameId: $authorPenNameId
        )
      }
    `
  })
}

/**
 * How many claims `getClaims` finds for one reader (`userId`) across every page, with the filters that read
 * honours: the admin's view of a reader's reviews numbers its pages from it. The page and its sort are not
 * part of the query. Admin only.
 */
const getUserClaimsCount = (args: ArcClaimGetArgs): Promise<number> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetUserClaimsCount (
        $userId: Int,
        $status: Int,
        $statuses: [Int!],
        $isDue: Boolean,
        $genericSearchTerm: String
      ) {
        userClaimsCount (
          userId: $userId,
          status: $status,
          statuses: $statuses,
          isDue: $isDue,
          genericSearchTerm: $genericSearchTerm
        )
      }
    `
  })
}

const getGroupedClaimsCount = (args: ArcClaimGetArgs): Promise<GroupedArcClaimResultDto> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetGroupedArcClaimsCount (
        $arcId: Int,
        $status: Int,
        $userSearchNeedle: String,
        $type: Int,
        $forAuthenticatedUser: Boolean,
        $reviewSiteStatus: Int,
        $mustHaveReviews: Boolean,
        $claimId: Int,
        $userId: Int,
        $groupByStatus: Int,
        $genericSearchTerm: String,
        $audienceTypes: [Int!],
        $categoryId: Int,
        $sortBy: String,
        $skip: Int,
        $take: Int,
        $countOnly: String,
      ) {
        groupedArcClaimsCount (
          arcId: $arcId,
          status: $status,
          userSearchNeedle: $userSearchNeedle,
          type: $type,
          forAuthenticatedUser: $forAuthenticatedUser,
          reviewSiteStatus: $reviewSiteStatus,
          mustHaveReviews: $mustHaveReviews,
          claimId: $claimId,
          userId: $userId,
          groupByStatus: $groupByStatus,
          genericSearchTerm: $genericSearchTerm,
          categoryId: $categoryId,
          audienceTypes: $audienceTypes,
          sortBy: $sortBy,
          skip: $skip,
          take: $take,
          countOnly: $countOnly,
        ) {
          activeCount,
          cancelledCount,
          completeCount,
          delayedCount,
          dueCount,
          pendingPublishingCount,
        }
      }
    `
  })
}

const getGroupedClaims = (args: ArcClaimGetArgs): Promise<GroupedArcClaimResultDto> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetGroupedArcClaims (
        $arcId: Int,
        $status: Int,
        $userSearchNeedle: String,
        $type: Int,
        $forAuthenticatedUser: Boolean,
        $reviewSiteStatus: Int,
        $mustHaveReviews: Boolean,
        $claimId: Int,
        $userId: Int,
        $groupByStatus: Int,
        $genericSearchTerm: String,
        $audienceTypes: [Int!],
        $categoryId: Int,
        $sortBy: String,
        $skip: Int,
        $take: Int
      ) {
        groupedArcClaims (
          arcId: $arcId,
          status: $status,
          userSearchNeedle: $userSearchNeedle,
          type: $type,
          forAuthenticatedUser: $forAuthenticatedUser,
          reviewSiteStatus: $reviewSiteStatus,
          mustHaveReviews: $mustHaveReviews,
          claimId: $claimId,
          userId: $userId,
          groupByStatus: $groupByStatus,
          genericSearchTerm: $genericSearchTerm,
          categoryId: $categoryId,
          audienceTypes: $audienceTypes,
          sortBy: $sortBy,
          skip: $skip,
          take: $take
        ) {
          activeCount,
          cancelledCount,
          completeCount,
          delayedCount,
          dueCount,
          pendingPublishingCount,
          result {
            totalRows,
            items {
              ...ArcClaimFragment,
              epubFile,
              pdfFile,
              arc {
                id,
                dueDate,
                dueCutOffDate,
                arcType,
                book {
                  id,
                  title,
                  penName,
                  authorPenName {
                    id,
                    name,
                    image,
                    followers {
                      id
                    }
                  },
                  bookCover,
                  description,
                  keywords,
                  epubPreviewFile,
                  hasEpubFile,
                  hasPdfFile
                },
                arcTeam {
                  id,
                  arcTeamMembers {
                    id
                    status,
                    arcTeamId,
                    user {
                      id
                    }
                  }
                }
              }
            }
          }
        }
      },
      ${arcFragments.claim}
    `
  }).then((result: any) => {
    return {
      ...result,
      result: {
        totalRows: result.result.totalRows,
        items: result.result.items.map((m: ArcClaimDto) => {
          return {
            ...getClaimResult(m),
            arc: getArcResult(m.arc)
          }
        })
      }
    }
  })
}

const grantReviewExemption = (args: ReviewExemptionArgs): Promise<ArcReviewSiteDto[]> => {
  return useApollo().mutate({
    variables: {
      ...args
    },
    mutation: gql`
      mutation GrantReviewExemption (
        $claimId: Int!,
        $grantSites: [Int!]
      ) {
        grantReviewExemption (claimId: $claimId, grantSites: $grantSites) {
          id
        }
      }
    `
  })
}

const revokeReviewExemption = (args: ReviewExemptionArgs): Promise<ArcReviewSiteDto[]> => {
  return useApollo().mutate({
    variables: {
      ...args
    },
    mutation: gql`
      mutation RevokeReviewExemption (
        $claimId: Int!,
        $revokeSites: [Int!]
      ) {
        revokeReviewExemption (claimId: $claimId, revokeSites: $revokeSites) {
          id
        }
      }
    `
  })
}

const claimArc = (args: CreateClaimDto): Promise<ArcClaimDto> => {
  return useApollo().mutate({
    variables: {
      ...args
    },
    mutation: gql`
      mutation ClaimArc (
        $arcId: Int!,
        $userId: Int!,
        $selectedAudioSiteFlag: Int
      ) {
        claimArc (
          arcId: $arcId,
          userId: $userId,
          selectedAudioSiteFlag: $selectedAudioSiteFlag
        ) {
          id
        }
      }
    `
  })
}

/**
 * Generic product-metric event (metric_tracker table). Fire-and-forget by
 * contract — tracking must never disrupt or block the flow it observes, so
 * failures are swallowed after a console warning.
 */
const trackMetric = async (type: string, metaData?: Record<string, unknown>): Promise<void> => {
  // Server-side this is auth-guarded and user-scoped, so a signed-out call (e.g. the public
  // review-copies feed) always 401s. Skip it rather than fire a guaranteed-Unauthorized request.
  if (!authStoreGetters.isAuthenticated) {
    return
  }

  try {
    await useApollo().mutate({
      variables: {
        type,
        metaData: metaData !== void 0 ? JSON.stringify(metaData) : void 0
      },
      mutation: gql`
        mutation TrackMetric (
          $type: String!,
          $metaData: String
        ) {
          trackMetric (
            type: $type,
            metaData: $metaData
          )
        }
      `
    })
  } catch (e) {
    console.error('[trackMetric] failed', e)
  }
}

/**
 * @param deliver where the downloaded bytes go. Defaults to `js-file-download`, which is a browser
 *   API: it clicks an `<a download href="blob:...">`, and the Capacitor WebView has no handler for
 *   that, so the click is swallowed and the reader is told a copy is coming that never arrives
 *   (sc-28415). The reviewer app passes a native-aware one. Injected rather than branched on here
 *   because the alternative imports `@capacitor/*` into common-app, which the website and admin
 *   bundles have no business carrying.
 */
const downloadClaim = (
  args: ClaimDownloadArgs,
  deliver: (data: Blob, fileName: string) => void | Promise<void> = fileDownload
): Promise<any> => {
  return useAxios().axios().post<ArcConvertedFileDto>('arc/download-claim', args, {
    responseType: 'blob',
    timeout: 600000 // 10 mins.
  }).then((r: AxiosResponse) => {
    const fileName = r.headers['content-disposition']?.split('filename=')[1]

    // Awaited, unlike the fire-and-forget browser path it replaces: natively this writes the file and
    // waits on the share sheet, and the caller's loading state has to cover both.
    return deliver(r.data, fileName)
  })
}

const emailClaim = (args: ClaimDownloadArgs): Promise<any> => {
  return useAxios().axios().post('arc/email-claim', args).then((_r: AxiosResponse & { data: ArcConvertedFileDto }) => {
    return
  })
}

const cancelClaim = (dto: CancelClaimDto): Promise<IdDto> => {
  return useApollo().mutate({
    variables: {
      ...dto
    },
    mutation: gql`
      mutation CancelClaim (
        $additionalInformation: String,
        $claimId: Int!,
        $reason: String,
        $type: Int!
      ) {
        cancelArcClaim (
          additionalInformation: $additionalInformation,
          claimId: $claimId,
          reason: $reason,
          type: $type
        ) {
          id
        }
      }
    `
  })
}

const unCancelClaim = (dto: UnCancelClaimDto): Promise<IdDto> => {
  return useApollo().mutate({
    variables: {
      ...dto
    },
    mutation: gql`
      mutation UnCancelClaim (
        $claimId: Int!
      ) {
        unCancelArcClaim (
          claimId: $claimId
        ) {
          id
        }
      }
    `
  })
}

const claimDateWithinMinutes = (date: Date | string, minutes: number): boolean => {
  const datePlusXMins = new Date(new Date(date).getTime() + minutes * 60000).getTime()
  return datePlusXMins > new Date().getTime()
}

const getNextArcToReview = (): Promise<ArcDto | null> => {
  return useApollo().query({
    query: gql`
      query GetNextArcToReview {
        getNextArcToReview {
          ...ArcFragment
        }
      }
      ${arcFragments.arc}
    `
  }).then((arcDto: any) => {
    return getArcResult(arcDto)
  })
}

const getClaimEligibility = async (arc: ArcDto, user: UserDto): Promise<ClaimEligibilityStatusDto> => {
  const { ...reasons } = bsConstants.ARCS.CLAIMS.INELIGIBLE_REASONS

  if (user.userEmails?.find(f => f.status === bsConstants.USER.EMAILS.STATUS.VERIFIED) === void 0) {
    return {
      ineligibleReason: reasons.EMAIL_NOT_VERIFIED
    }
  }

  return getClaimEligibilityForUser({
    arcId: arc.id,
    suggestArc: true
  })
}

const getClaimEligibilityForUser = async (args: GetClaimEligibilityStatusArgs): Promise<ClaimEligibilityStatusDto> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetClaimEligibilityForUser (
        $arcId: Int!,
        $suggestArc: Boolean!
      ) {
        getClaimEligibilityForUser (
          arcId: $arcId,
          suggestArc: $suggestArc
        ) {
          ineligibleReason,
          reasonDate,
          reasonDescription,
          existingClaim {
            ...ArcClaimFragment
          },
          suggestedArc {
            ...ArcFragment
          },
          suggestedBooks {
            ...BookFragment
          }
        }
      }
      ${arcFragments.claim}
      ${arcFragments.arc},
      ${bookFragments.book}
    `
  }).then((result: any) => {
    return {
      ...result,
      suggestedArc: getArcResult(result.suggestedArc)
    }
  })
}

const updateDueCutOffDate = (arc: ArcDto): Promise<ArcDto> => {
  if (!arc.bookId) {
    const error = new Error('UpdateDueCutOffDate called without bookId')
    Sentry.withScope(scope => {
      scope.setTag('operation', 'UpdateDueCutOffDate')
      scope.setContext('arc', { id: arc.id, bookId: arc.bookId })
      Sentry.captureException(error)
    })
    return Promise.reject(error)
  }
  return useApollo().mutate({
    variables: {
      id: arc.id,
      bookId: arc.bookId
    },
    mutation: gql`
      mutation UpdateDueCutOffDate (
        $id: Int!,
        $bookId: Int!
      ) {
        updateDueCutOffDate (
          id: $id,
          bookId: $bookId
        ) {
          ...ArcFragment
        }
      }
      ${arcFragments.arc}
    `
  })
}

const getClaimSites = (claim: ArcClaimDto): ArcReviewSiteDto[] => {
  const result: ArcReviewSiteDto[] = []
  if (!claim?.review?.sites) {
    return result
  }

  const requireLinkSites = [
    bsConstants.ARCS.REVIEWS.FLAGS.AMAZON,
    bsConstants.ARCS.REVIEWS.FLAGS.BOOK_BUB,
    bsConstants.ARCS.REVIEWS.FLAGS.AUDIO_BOOK_BUB,
    bsConstants.ARCS.REVIEWS.FLAGS.GOOD_READS,
    bsConstants.ARCS.REVIEWS.FLAGS.AUDIO_GOODREADS,
    bsConstants.ARCS.REVIEWS.FLAGS.STORY_GRAPH,
    bsConstants.ARCS.REVIEWS.FLAGS.AUDIO_STORY_GRAPH
  ]

  for (const site of claim.review.sites.filter(f => f.status !== bsConstants.ARCS.REVIEWS.SITE.STATUS.NONE || f.exempt)) {
    const thisSite = { ...site }
    // modify review links to include book link
    // for sites which don't require a link (like Google Play)
    if (!requireLinkSites.includes(thisSite.site)) {
      const siteBookKey: string = (bsConstants.ARCS.REVIEWS.FLAG_BOOK_KEYS_SITE as HasIndex)[site.site]
      const authorBookLink = claim?.arc?.book ? (claim.arc.book as HasIndex)[siteBookKey] : ''

      if (authorBookLink) {
        thisSite.link = authorBookLink
      }
    }

    result.push(thisSite)
  }

  return result
}

interface ExemptableSite {
  name: string
  site: number
  selected: boolean
  disabled: boolean
}

const getAllowedExemptSitesForClaim = (claim: ArcClaimDto): ExemptableSite[] => {
  const sites = []
  // If the reviewer only needs to say yes/no to "prove" their review on a store,
  // then the author should not be able to grant exemptions for that store
  const { SMASH_WORDS, KOBO, BARNES, GOOGLE, ITUNES, AUDIBLE_UK, AUDIBLE_US, AUDIBLE } = bsConstants.ARCS.REVIEWS.FLAGS
  const excludeSites = SMASH_WORDS | KOBO | BARNES | GOOGLE | ITUNES | AUDIBLE_UK | AUDIBLE_US | AUDIBLE

  // Get the sites which have been set against the claim (don't show them all).

  // Only list stores that the author has marked as required, or optionally required.
  // If the store is 100% optional, then don't let authors grant an exemption for it.
  let arcSites = getArcSitesFromFlags(claim.reviewFlags, excludeSites)
  const requiredFlagCount = countSiteFlags(claim.reviewFlags)
  if (claim.requiredReviewCount || 0 > requiredFlagCount) {
    arcSites = arcSites.concat(getArcSitesFromFlags(claim.optionalReviewFlags || 0, excludeSites))
  }

  // Build our data
  for (const arcSite of arcSites) {
    const reviewSite = claim.review?.sites?.find(s => s.site === arcSite.site)
    const hasExemption = !!(reviewSite && reviewSite.exempt)
    const hasReviewedAlready = !!reviewSite && isValidReviewLink(reviewSite.site, reviewSite.link || '')
    // const hasBeenRejected = !!reviewSite && reviewSite.status === bsConstants.ARCS.REVIEWS.SITE.STATUS.REJECTED
    const hasBeenDelayed = !!reviewSite && reviewSite.status === bsConstants.ARCS.REVIEWS.SITE.STATUS.DELAYED

    sites.push({
      name: arcSite.description,
      site: arcSite.site,
      // Do any of the sites passed in already have review exemptions? Set them here.
      selected: hasExemption,
      disabled: hasReviewedAlready && !hasBeenDelayed
    })
  }

  return sites
}

const markClaimAsRead = (id: number): Promise<any> => {
  return updateClaimState(id, bsConstants.ARCS.CLAIMS.STATE.READ)
}

const markClaimAsUnRead = (id: number): Promise<any> => {
  return updateClaimState(id, bsConstants.ARCS.CLAIMS.STATE.NEW)
}

const markClaimAsNewActivity = (id: number): Promise<any> => {
  return updateClaimState(id, bsConstants.ARCS.CLAIMS.STATE.NEW_ACTIVITY)
}

const updateClaimState = (id: number, state: number): Promise<any> => {
  return useApollo().mutate({
    variables: {
      claimId: id,
      state
    },
    mutation: gql`
      mutation UpdateClaimState (
        $claimId: Int!,
        $state: Int!
      ) {
        updateClaimState (
          claimId: $claimId,
          state: $state
        )
      }
    `
  })
}

const markReviewSiteAsSeen = (id: number): Promise<any> => {
  return useApollo().mutate({
    variables: {
      id
    },
    mutation: gql`
      mutation MarkReviewSiteAsSeen (
        $id: Int!
      ) {
        markReviewSiteAsSeen (
          id: $id
        ) {
          id,
          link,
          site,
          status,
          statusDate,
          statusReason,
          exempt,
          state
        }
      }
    `
  })
}


const crud = useBaseCrud<ArcClaimDto>('arc')

/**
 * Chained the same way as the other two, so it returns this bag.
 */
const service = {
  ...crud,

  claimArc,
  getClaim,
  getClaimToReview,
  getClaims,
  getArcOwnerClaimsCount,
  getUserClaimsCount,
  getGroupedClaimsCount,
  getGroupedClaims,
  updateClaimReadLocation,
  cancelClaim,
  unCancelClaim,
  markClaimAsRead,
  markClaimAsUnRead,
  markClaimAsNewActivity,
  updateClaimState,
  getClaimEligibility,
  getClaimEligibilityForUser,
  getNextArcToReview,
  downloadClaim,
  emailClaim,
  getClaimSites,
  getAllowedExemptSitesForClaim,
  claimDateWithinMinutes,
  updateDueCutOffDate,
  trackMetric,
  markReviewSiteAsSeen,
  grantReviewExemption,
  revokeReviewExemption,

  globalLoader (enable: boolean) {
    crud.setGlobalLoader(enable)
    return service
  }
}

export function arcClaimService () {
  return service
}
